import type { ResourceAmounts } from '../buildings/definitions';
import type { OwnerId, PlayerId } from '../ids';

/**
 * Tech tree (D128): researched at a Research Lab, one project at a time per
 * colony, paid up front. Each research is a small, readable bonus that the
 * systems ask for through `bonus(owner, key)` — a multiplier (or an addition
 * for `*_add` keys). Deterministic and part of the snapshot.
 */
export type BonusKey =
  | 'damage_bullet'
  | 'damage_fire'
  | 'damage_melee'
  | 'unit_hp'
  | 'bite'
  | 'train_speed'
  | 'turret_range_add'
  | 'turret_cooldown'
  | 'wall_armor_add'
  | 'prod_food'
  | 'prod_wood'
  | 'prod_scrap'
  | 'power';

export type ResearchId =
  | 'sharp_rounds'
  | 'ap_rounds'
  | 'incendiary'
  | 'combat_training'
  | 'body_armor'
  | 'vaccine'
  | 'drill_sergeants'
  | 'tall_towers'
  | 'rapid_fire'
  | 'stone_walls'
  | 'crop_rotation'
  | 'power_saws'
  | 'scrap_presses'
  | 'efficient_generators';

export interface ResearchDef {
  readonly id: ResearchId;
  readonly name: string;
  readonly effect: string;
  readonly cost: ResourceAmounts;
  readonly seconds: number;
  readonly requires?: ResearchId;
  readonly bonus: Readonly<Partial<Record<BonusKey, number>>>;
  /** Column in the tree (for the UI): 0 weapons, 1 soldiers, 2 defences, 3 economy. */
  readonly branch: 0 | 1 | 2 | 3;
}

const R = (d: ResearchDef): ResearchDef => d;

export const RESEARCH: readonly ResearchDef[] = [
  R({ id: 'sharp_rounds', name: 'Sharp Rounds', effect: '+20% rifle damage', cost: { wood: 30, scrap: 40 }, seconds: 40, bonus: { damage_bullet: 1.2 }, branch: 0 }),
  R({ id: 'ap_rounds', name: 'Armour-Piercing Rounds', effect: '+20% more rifle damage', cost: { scrap: 90 }, seconds: 60, requires: 'sharp_rounds', bonus: { damage_bullet: 1.2 }, branch: 0 }),
  R({ id: 'incendiary', name: 'Incendiary Fuel', effect: '+30% fire damage', cost: { wood: 40, scrap: 60 }, seconds: 50, requires: 'sharp_rounds', bonus: { damage_fire: 1.3 }, branch: 0 }),
  R({ id: 'combat_training', name: 'Close Combat', effect: '+25% melee damage', cost: { wood: 40, scrap: 30 }, seconds: 40, bonus: { damage_melee: 1.25 }, branch: 1 }),
  R({ id: 'body_armor', name: 'Body Armour', effect: '+20% health for new soldiers', cost: { wood: 30, scrap: 60 }, seconds: 50, bonus: { unit_hp: 1.2 }, branch: 1 }),
  R({ id: 'vaccine', name: 'Field Vaccine', effect: 'Bites infect half as fast', cost: { food: 80, scrap: 60 }, seconds: 70, requires: 'body_armor', bonus: { bite: 0.5 }, branch: 1 }),
  R({ id: 'drill_sergeants', name: 'Drill Sergeants', effect: 'Soldiers train 33% faster', cost: { wood: 60, food: 40 }, seconds: 45, requires: 'combat_training', bonus: { train_speed: 1.33 }, branch: 1 }),
  R({ id: 'tall_towers', name: 'Tall Towers', effect: 'Towers reach 1 tile further', cost: { wood: 70, scrap: 30 }, seconds: 45, bonus: { turret_range_add: 1 }, branch: 2 }),
  R({ id: 'rapid_fire', name: 'Rapid Fire', effect: 'Towers fire 20% faster', cost: { scrap: 90 }, seconds: 60, requires: 'tall_towers', bonus: { turret_cooldown: 0.8 }, branch: 2 }),
  R({ id: 'stone_walls', name: 'Stone Walls', effect: 'Walls and gates +3 armour', cost: { wood: 50, scrap: 50 }, seconds: 50, bonus: { wall_armor_add: 3 }, branch: 2 }),
  R({ id: 'efficient_generators', name: 'Efficient Generators', effect: '+50% power from Generators', cost: { scrap: 70 }, seconds: 50, requires: 'tall_towers', bonus: { power: 1.5 }, branch: 2 }),
  R({ id: 'crop_rotation', name: 'Crop Rotation', effect: 'Farms produce 25% more', cost: { wood: 40, food: 30 }, seconds: 40, bonus: { prod_food: 1.25 }, branch: 3 }),
  R({ id: 'power_saws', name: 'Power Saws', effect: 'Sawmills produce 25% more', cost: { wood: 30, scrap: 40 }, seconds: 40, bonus: { prod_wood: 1.25 }, branch: 3 }),
  R({ id: 'scrap_presses', name: 'Scrap Presses', effect: 'Scrap Yards produce 25% more', cost: { wood: 50, scrap: 30 }, seconds: 45, requires: 'power_saws', bonus: { prod_scrap: 1.25 }, branch: 3 }),
];

export function researchDef(id: ResearchId): ResearchDef {
  const d = RESEARCH.find((r) => r.id === id);
  if (!d) throw new Error(`Unknown research ${id}`);
  return d;
}

export function isResearchId(v: unknown): v is ResearchId {
  return typeof v === 'string' && RESEARCH.some((r) => r.id === v);
}

export type ResearchEvent = { type: 'started' | 'done'; playerId: PlayerId; research: ResearchId };

export class ResearchStore {
  private readonly done = new Map<PlayerId, Set<ResearchId>>();
  private readonly current = new Map<PlayerId, { id: ResearchId; progress: number }>();
  private readonly listeners = new Set<(e: ResearchEvent) => void>();

  subscribe(l: (e: ResearchEvent) => void): () => void {
    this.listeners.add(l);
    return () => this.listeners.delete(l);
  }

  has(p: PlayerId, id: ResearchId): boolean {
    return this.done.get(p)?.has(id) ?? false;
  }

  researched(p: PlayerId): ResearchId[] {
    return RESEARCH.map((r) => r.id).filter((id) => this.has(p, id));
  }

  active(p: PlayerId): { id: ResearchId; progress: number } | null {
    const c = this.current.get(p);
    return c ? { ...c } : null;
  }

  /** Multiplier (or addition for `*_add` keys) from everything this owner has researched. */
  bonus(owner: OwnerId, key: BonusKey): number {
    const add = key.endsWith('_add');
    let v = add ? 0 : 1;
    const set = this.done.get(owner as PlayerId);
    if (!set) return v;
    for (const r of RESEARCH) {
      const b = r.bonus[key];
      if (b === undefined || !set.has(r.id)) continue;
      v = add ? v + b : v * b;
    }
    return v;
  }

  start(p: PlayerId, id: ResearchId): void {
    this.current.set(p, { id, progress: 0 });
    this.emit({ type: 'started', playerId: p, research: id });
  }

  /** Advance every colony's project; `hasLab` = it still has a complete Research Lab (else it pauses). */
  step(dt: number, hasLab: (p: PlayerId) => boolean): void {
    for (const [p, c] of [...this.current].sort((a, b) => (a[0] < b[0] ? -1 : 1))) {
      if (!hasLab(p)) continue;
      c.progress += dt;
      if (c.progress < researchDef(c.id).seconds - 1e-9) continue;
      this.current.delete(p);
      if (!this.done.has(p)) this.done.set(p, new Set());
      this.done.get(p)!.add(c.id);
      this.emit({ type: 'done', playerId: p, research: c.id });
    }
  }

  snapshot(): { done: Record<string, ResearchId[]>; current: Record<string, { id: ResearchId; progress: number }> } {
    const done: Record<string, ResearchId[]> = {};
    for (const [p, set] of [...this.done].sort((a, b) => (a[0] < b[0] ? -1 : 1))) done[p] = RESEARCH.map((r) => r.id).filter((id) => set.has(id));
    const current: Record<string, { id: ResearchId; progress: number }> = {};
    for (const [p, c] of [...this.current].sort((a, b) => (a[0] < b[0] ? -1 : 1))) current[p] = { ...c };
    return { done, current };
  }

  private emit(e: ResearchEvent): void {
    for (const l of this.listeners) l(e);
  }
}
